import { IconChevronDown } from '@tabler/icons-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useId, useState } from 'react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { DUR, EASE, PRESS } from '../../interaction';

export type AccordionType = 'single' | 'multiple';
export type AccordionVariant = 'plain' | 'bordered' | 'separated';

export interface AccordionItemData {
  id: string;
  /** The visible header text. */
  header: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface AccordionProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  items: AccordionItemData[];
  /** `single` collapses the others when one opens. */
  type?: AccordionType;
  /** Controlled list of open item ids. */
  open?: string[];
  defaultOpen?: string[];
  onOpenChange?: (open: string[]) => void;
  variant?: AccordionVariant;
  /** Heading level for each item's header, so the page outline stays sane. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** Degrees the control icon rotates to when the item is open. */
  openRotation?: number;
}

const OUTER: Record<AccordionVariant, string> = {
  plain: '',
  // No `overflow-hidden`: the focus ring is drawn 2px outside the header and
  // would be clipped on the first and last rows. The 2px corner gap the radius
  // leaves is imperceptible; a clipped ring is not.
  bordered: 'rounded-xs border border-line-subtle',
  separated: 'flex flex-col gap-2',
};

const ITEM: Record<AccordionVariant, string> = {
  plain: 'border-b border-line-subtle last:border-b-0',
  bordered: 'border-b border-line-subtle last:border-b-0',
  separated: 'rounded-xs border border-line-subtle',
};

export function Accordion({
  items,
  type = 'single',
  open,
  defaultOpen,
  onOpenChange,
  variant = 'plain',
  headingLevel = 3,
  openRotation = 180,
  className,
  ...rest
}: AccordionProps) {
  const reduceMotion = useReducedMotion();
  const baseId = useId();
  const [uncontrolled, setUncontrolled] = useState<string[]>(defaultOpen ?? []);
  const openIds = open ?? uncontrolled;

  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  function toggle(id: string) {
    const isOpen = openIds.includes(id);
    const next = isOpen
      ? openIds.filter((entry) => entry !== id)
      : type === 'single'
        ? [id]
        : [...openIds, id];
    if (open === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  }

  return (
    <div {...rest} className={cn('min-w-0', OUTER[variant], className)}>
      {items.map((item) => {
        const isOpen = openIds.includes(item.id);
        const headerId = `${baseId}-header-${item.id}`;
        const panelId = `${baseId}-panel-${item.id}`;

        return (
          <div key={item.id} className={cn('min-w-0', ITEM[variant])}>
            <Heading className="m-0">
              <button
                type="button"
                id={headerId}
                // Kept even while the panel is unmounted mid-transition: the
                // relationship is the button's, and `aria-expanded` carries the
                // state that actually changes.
                aria-controls={panelId}
                aria-expanded={isOpen}
                aria-disabled={item.disabled || undefined}
                onClick={item.disabled ? undefined : () => toggle(item.id)}
                className={cn(
                  // 44px floor, so the header never needs the `dhd-target` overlay.
                  'flex min-h-11 w-full min-w-0 items-center gap-3 px-4 py-3 text-left',
                  'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  // Disabled is a token, not an opacity. `opacity-50` on the row
                  // halved the chevron's `--text-muted`, which measured 2.36:1
                  // in light and 2.87:1 in dark — under the 3:1 floor SC 1.4.11
                  // sets for a UI component's own part. Muting the title and
                  // dropping the hover does the same job at full opacity.
                  item.disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-surface-muted',
                  !item.disabled && PRESS,
                )}
              >
                <span
                  className={cn(
                    'min-w-0 flex-1 font-display text-sm font-semibold tracking-title uppercase',
                    item.disabled ? 'text-muted' : 'text-heading',
                  )}
                >
                  {item.header}
                </span>
                <motion.span
                  aria-hidden="true"
                  initial={false}
                  animate={{ rotate: isOpen ? openRotation : 0 }}
                  transition={reduceMotion ? { duration: 0 } : { duration: DUR.base, ease: EASE.overshoot }}
                  className="grid size-5 shrink-0 place-items-center text-muted"
                >
                  <IconChevronDown className="size-4" />
                </motion.span>
              </button>
            </Heading>

            <AnimatePresence initial={false}>
              {isOpen ? (
                <motion.div
                  key="panel"
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={
                    reduceMotion ? { duration: 0 } : { duration: DUR.slow, ease: EASE.out }
                  }
                  className="overflow-hidden"
                >
                  {/* Same rule as Panel's body: the measure lands on the
                      paragraphs, so a demo that puts a table in a panel is not
                      clipped. Measured on /components, the longest accordion
                      paragraph ran to 161 characters on one line at 13px. */}
                  <div className="min-w-0 px-4 pb-4 text-sm text-body [&_p]:max-w-[65ch]">
                    {item.content}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
